import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@mlm/ui/lib/cn';
import { RichText } from '../components/coach/rich-text';
import { ConfirmDialog } from '../components/confirm-dialog';
import {
  Chip,
  CoachButton,
  ErrorNote,
  Eyebrow,
  inputClass,
  Loading,
  Screen,
} from '../components/primitives';
import { coaching, type JournalEntry } from '../lib/api';
import { longDay } from '../lib/format';
import { qk, useIsPro, useRefreshCoaching } from '../lib/queries';
import { ProLock } from '../components/pro-lock';

const PAGE = 20;

const MOODS = [
  { value: 1, label: 'Struggling' },
  { value: 2, label: 'Low' },
  { value: 3, label: 'Okay' },
  { value: 4, label: 'Good' },
  { value: 5, label: 'Great' },
] as const;
const moodLabel = (mood: number | null) => MOODS.find((m) => m.value === mood)?.label ?? null;

const KIND_LABEL: Record<JournalEntry['kind'], string | null> = {
  free: null,
  prompt: null,
  gratitude: 'Gratitude',
  evening: 'Evening review',
};

/** SC-24: today's prompt or free writing, past entries, and a reflection from Master Kala. */
export function JournalPage() {
  const qc = useQueryClient();
  const refresh = useRefreshCoaching();
  const promptQ = useQuery({
    queryKey: qk.journalPrompt,
    queryFn: coaching.journalPrompt,
    staleTime: 5 * 60_000,
  });
  const entries = useInfiniteQuery({
    queryKey: qk.journal,
    queryFn: ({ pageParam }) => coaching.journal({ limit: PAGE, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.length === PAGE ? last.at(-1)?.createdAt : undefined),
  });
  const [mode, setMode] = useState<'prompt' | 'free'>('prompt');
  const [promptIndex, setPromptIndex] = useState(0);
  const [content, setContent] = useState('');
  const [mood, setMood] = useState<number | null>(null);
  // Which entry the confirm dialog is asking about. It lives here, not on the
  // row, because the delete mutation does.
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const prompts = promptQ.data ? [promptQ.data.prompt, ...promptQ.data.alternatives] : [];
  const prompt =
    mode === 'prompt' ? (prompts[promptIndex % Math.max(prompts.length, 1)] ?? null) : null;

  const create = useMutation({
    mutationFn: () =>
      coaching.createJournalEntry({
        kind: prompt ? 'prompt' : 'free',
        prompt: prompt ?? undefined,
        content: content.trim(),
        mood: mood ?? undefined,
      }),
    meta: { successMessage: 'Saved to your journal.' },
    onSuccess: () => {
      coaching.event('journal_entry_created', { kind: prompt ? 'prompt' : 'free' });
      setContent('');
      setMood(null);
      void refresh();
    },
  });
  const reflect = useMutation({
    mutationFn: (id: string) => coaching.reflectJournalEntry(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.journal }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => coaching.deleteJournalEntry(id),
    meta: { successMessage: 'Entry deleted.' },
    onSuccess: () => {
      setConfirmId(null);
      void refresh();
    },
    onError: () => setConfirmId(null),
  });

  const list = entries.data?.pages.flat() ?? [];

  return (
    <Screen
      back="/manifest"
      eyebrow="Manifest"
      title="Journal"
      subtitle="Write what is true today. Master Kala can reflect it back to you."
    >
      <section className="border-b border-border px-5 py-6 lg:py-8">
        <div className="flex flex-wrap gap-2" role="group" aria-label="What to write">
          <Chip selected={mode === 'prompt'} onClick={() => setMode('prompt')}>
            Today's prompt
          </Chip>
          <Chip selected={mode === 'free'} onClick={() => setMode('free')}>
            Free writing
          </Chip>
        </div>
        {mode === 'prompt' && (
          <div className="mt-6">
            {promptQ.isPending ? (
              <p className="eyebrow animate-pulse">Loading</p>
            ) : prompt ? (
              <>
                <p className="font-display text-[1.35rem] leading-snug text-ink lg:text-[1.6rem]">
                  {prompt}
                </p>
                {prompts.length > 1 && (
                  <CoachButton
                    kind="quiet"
                    className="mt-3"
                    onClick={() => setPromptIndex((i) => i + 1)}
                  >
                    Show another prompt
                  </CoachButton>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No prompt is available right now. Free writing works just as well.
              </p>
            )}
          </div>
        )}
        <label htmlFor="journal-content" className="sr-only">
          Your entry
        </label>
        <textarea
          id="journal-content"
          rows={6}
          maxLength={4000}
          value={content}
          placeholder="Start writing"
          onChange={(e) => setContent(e.target.value)}
          className={cn(inputClass, 'mt-6 min-h-36 resize-y leading-relaxed')}
        />
        <fieldset className="mt-5">
          <legend className="eyebrow mb-2">How do you feel?</legend>
          <div className="flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <Chip
                key={m.value}
                selected={mood === m.value}
                onClick={() => setMood(mood === m.value ? null : m.value)}
              >
                {m.label}
              </Chip>
            ))}
          </div>
        </fieldset>
        <CoachButton
          kind="gold"
          className="mt-7"
          disabled={!content.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          Save entry
        </CoachButton>
      </section>

      <Eyebrow className="border-b border-border px-5 pb-3 pt-7">Past entries</Eyebrow>
      {entries.isPending ? (
        <Loading />
      ) : entries.isError ? (
        <ErrorNote error={entries.error} retry={() => void entries.refetch()} />
      ) : list.length === 0 ? (
        <p className="px-5 py-5 text-sm text-muted-foreground">Your entries will appear here.</p>
      ) : (
        <ol>
          {list.map((entry) => (
            <EntryItem
              key={entry.id}
              entry={entry}
              reflecting={reflect.isPending && reflect.variables === entry.id}
              deleting={remove.isPending && remove.variables === entry.id}
              onReflect={() => reflect.mutate(entry.id)}
              onDelete={() => setConfirmId(entry.id)}
            />
          ))}
        </ol>
      )}
      {entries.hasNextPage && (
        <div className="px-5 py-5">
          <CoachButton
            kind="quiet"
            disabled={entries.isFetchingNextPage}
            onClick={() => void entries.fetchNextPage()}
          >
            Show older entries
          </CoachButton>
        </div>
      )}
      <ConfirmDialog
        open={confirmId !== null}
        title="Delete this entry?"
        body="It leaves your journal for good, and any reflection Master Kala wrote on it goes with it."
        confirmLabel="Delete it"
        busy={remove.isPending}
        onConfirm={() => confirmId && remove.mutate(confirmId)}
        onCancel={() => setConfirmId(null)}
      />
    </Screen>
  );
}

function EntryItem({
  entry,
  reflecting,
  deleting,
  onReflect,
  onDelete,
}: {
  entry: JournalEntry;
  reflecting: boolean;
  deleting: boolean;
  onReflect: () => void;
  onDelete: () => void;
}) {
  // The lock renders in place of the button, so a free member is never offered
  // something that would only come back refused.
  const isPro = useIsPro();
  const meta = [longDay(entry.day), KIND_LABEL[entry.kind], moodLabel(entry.mood)]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="border-b border-border px-5 py-5">
      <p className="eyebrow">{meta}</p>
      {entry.prompt && (
        <p className="mt-2 text-sm italic leading-relaxed text-sage">{entry.prompt}</p>
      )}
      <p className="mt-2 whitespace-pre-wrap break-words text-[0.95rem] leading-relaxed text-ink/90 lg:max-w-prose">
        {entry.content}
      </p>
      {entry.aiReflection && (
        <div className="mt-4 border-t border-border pt-3 lg:max-w-prose">
          <Eyebrow className="mb-1.5">Master Kala reflects</Eyebrow>
          <div className="text-[0.93rem] leading-relaxed text-ink/80">
            <RichText text={entry.aiReflection} />
          </div>
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        {!entry.aiReflection &&
          entry.kind !== 'evening' &&
          (isPro ? (
            <CoachButton kind="quiet" disabled={reflecting} onClick={onReflect}>
              {reflecting ? 'Master Kala is reflecting' : 'Reflect with Master Kala'}
            </CoachButton>
          ) : (
            <ProLock feature="journal_reflection" compact />
          ))}
        <CoachButton kind="quiet" disabled={deleting} onClick={onDelete}>
          {deleting ? 'Deleting' : 'Delete'}
        </CoachButton>
      </div>
    </li>
  );
}
